import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const RSVP_TOKEN_KEY = 'weddingRsvpAccessToken';
const RSVP_DEADLINE = '[RSVP DEADLINE]';
const MEALS = [
  { value: 'steak', label: 'Steak' },
  { value: 'shrimp', label: 'Shrimp' },
  { value: 'vegetarian', label: 'Vegetarian' },
];

const tokenHeaders = (token) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${token}`,
});

const fullName = (response) =>
  `${response.first_name || ''} ${response.last_name || ''}`.trim();

const createResponse = (member, existing) => ({
  member_id: member.id,
  first_name: member.first_name,
  last_name: member.last_name,
  attendance: existing?.attendance || '',
  welcome_party: existing?.welcome_party || '',
  meal_choice: existing?.meal_choice || '',
  dietary_restrictions: existing?.dietary_restrictions || '',
});

const Progress = ({ current }) => {
  const steps = [
    { id: 'attendance', label: 'Events' },
    { id: 'details', label: 'Details' },
    { id: 'review', label: 'Review' },
  ];
  const currentIndex = Math.max(0, steps.findIndex((step) => step.id === current));

  return (
    <ol className="rsvp-progress" aria-label="RSVP progress">
      {steps.map((step, index) => (
        <li
          key={step.id}
          className={index <= currentIndex ? 'is-active' : ''}
          aria-current={step.id === current ? 'step' : undefined}
        >
          <span>{index + 1}</span>
          {step.label}
        </li>
      ))}
    </ol>
  );
};

const InvitationLookup = ({
  accessToken,
  onAccessToken,
  onHouseholdFound,
  onNotFound,
}) => {
  const [accessCode, setAccessCode] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDisambiguation, setShowDisambiguation] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!accessToken && !accessCode.trim()) {
      setError('Enter the access code from your invitation.');
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      setError('Enter both a first and last name.');
      return;
    }

    setIsLoading(true);
    try {
      let activeToken = accessToken;
      if (!activeToken) {
        const verification = await fetch(`${API}/rsvp/verify-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password: accessCode.trim() }),
        });

        if (!verification.ok) {
          const verificationError = await verification.json().catch(() => ({}));
          setError(
            verification.status === 401
              ? 'That access code does not match. Please check your invitation.'
              : verificationError.detail ||
                  'We could not verify the access code. Please try again.'
          );
          return;
        }

        const verificationData = await verification.json();
        activeToken = verificationData.access_token;
        sessionStorage.setItem(RSVP_TOKEN_KEY, activeToken);
        onAccessToken(activeToken);
      }

      const response = await fetch(`${API}/rsvp/search`, {
        method: 'POST',
        headers: tokenHeaders(activeToken),
        body: JSON.stringify({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
        }),
      });

      if (response.status === 401) {
        sessionStorage.removeItem(RSVP_TOKEN_KEY);
        onAccessToken('');
        setAccessCode('');
        setError('Your RSVP session expired. Please enter the access code again.');
        return;
      }
      if (!response.ok) {
        throw new Error('Guest search failed');
      }

      const data = await response.json();
      if (!data.found || data.households.length === 0) {
        onNotFound({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          token: activeToken,
        });
      } else if (data.households.length === 1) {
        onHouseholdFound(data.households[0], {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
        });
      } else {
        setSearchResults(data.households);
        setShowDisambiguation(true);
      }
    } catch (lookupError) {
      setError('We could not find your invitation right now. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="rsvp-step max-w-lg mx-auto">
      <div className="text-center mb-8">
        <p className="eyebrow text-olive mb-4">Your Invitation</p>
        <h2 className="font-giaza text-3xl md:text-4xl text-forest mb-4">
          Find Your Invitation
        </h2>
        <p className="rsvp-step__copy">
          {accessToken
            ? 'Enter the name of anyone listed on your invitation.'
            : 'Use the access code from your invitation, then enter the name of anyone in your household.'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="rsvp-form space-y-6">
        {!accessToken && (
          <div>
            <Label htmlFor="rsvp-access-code">Invitation access code</Label>
            <Input
              id="rsvp-access-code"
              type="password"
              value={accessCode}
              onChange={(event) => setAccessCode(event.target.value)}
              autoComplete="off"
              placeholder="Access code"
              autoFocus
            />
          </div>
        )}

        <div className="rsvp-name-grid">
          <div>
            <Label htmlFor="rsvp-first-name">First name</Label>
            <Input
              id="rsvp-first-name"
              type="text"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              autoComplete="given-name"
              placeholder="First name"
              autoFocus={Boolean(accessToken)}
            />
          </div>
          <div>
            <Label htmlFor="rsvp-last-name">Last name</Label>
            <Input
              id="rsvp-last-name"
              type="text"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              autoComplete="family-name"
              placeholder="Last name"
            />
          </div>
        </div>

        {error && (
          <p className="rsvp-error" role="alert">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full disabled:opacity-50"
        >
          {isLoading ? 'Finding Your Invitation…' : 'Find My Invitation'}
        </button>
        <p className="text-center text-sm text-forest/60">
          If your name has changed or you use a nickname, try another invited
          member of your household.
        </p>
      </form>

      <Dialog open={showDisambiguation} onOpenChange={setShowDisambiguation}>
        <DialogContent className="rsvp-dialog bg-linen border-forest/20">
          <DialogHeader>
            <DialogTitle className="font-giaza text-2xl text-forest">
              Which invitation is yours?
            </DialogTitle>
          </DialogHeader>
          <p className="text-forest/70 mb-4">
            We found more than one match. Select the correct household.
          </p>
          <div className="space-y-3">
            {searchResults.map((household) => (
              <button
                key={household.id}
                type="button"
                onClick={() => {
                  setShowDisambiguation(false);
                  onHouseholdFound(household, {
                    firstName: firstName.trim(),
                    lastName: lastName.trim(),
                  });
                }}
                className="w-full p-5 text-left border border-forest/20 hover:border-forest hover:bg-mist/60 transition-all"
              >
                <span className="block font-medium text-forest">
                  {household.label || household.household_label}
                </span>
                <span className="block text-base text-forest/70 mt-1">
                  {(household.guests || household.members || [])
                    .map((member) => `${member.first_name} ${member.last_name}`)
                    .join(', ')}
                </span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ContactCoupleStep = ({ guestInfo, onBack }) => {
  const [formData, setFormData] = useState({
    name: `${guestInfo.firstName} ${guestInfo.lastName}`,
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API}/contact-couple`, {
        method: 'POST',
        headers: tokenHeaders(guestInfo.token),
        body: JSON.stringify(formData),
      });
      if (!response.ok) {
        throw new Error('Message failed');
      }
      setSubmitted(true);
    } catch (error) {
      toast.error('We could not save your message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="rsvp-step max-w-md mx-auto text-center">
        <div className="rsvp-success-mark" aria-hidden="true">
          ✓
        </div>
        <h2 className="font-giaza text-3xl text-forest mb-4">Message Received</h2>
        <p className="rsvp-step__copy">
          Your note has been saved for Lexi and James. They will follow up using
          the email address you provided.
        </p>
      </div>
    );
  }

  return (
    <div className="rsvp-step max-w-lg mx-auto">
      <div className="text-center mb-8">
        <p className="eyebrow text-olive mb-4">Let Us Help</p>
        <h2 className="font-giaza text-3xl text-forest mb-4">
          We Couldn’t Find That Name
        </h2>
        <p className="rsvp-step__copy">
          Check the spelling or try another invited member’s name. You can also
          leave Lexi and James a note here.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="rsvp-form space-y-6">
        <div>
          <Label htmlFor="contact-name">Your name</Label>
          <Input
            id="contact-name"
            type="text"
            value={formData.name}
            onChange={(event) =>
              setFormData((current) => ({ ...current, name: event.target.value }))
            }
            required
          />
        </div>
        <div>
          <Label htmlFor="contact-email">Email address</Label>
          <Input
            id="contact-email"
            type="email"
            value={formData.email}
            onChange={(event) =>
              setFormData((current) => ({ ...current, email: event.target.value }))
            }
            autoComplete="email"
            placeholder="you@example.com"
            required
          />
        </div>
        <div>
          <Label htmlFor="contact-message">Message</Label>
          <Textarea
            id="contact-message"
            value={formData.message}
            onChange={(event) =>
              setFormData((current) => ({ ...current, message: event.target.value }))
            }
            placeholder="Tell us how we can help."
            rows={4}
          />
        </div>
        <div className="rsvp-actions">
          <button type="button" onClick={onBack} className="btn-outline">
            Try Another Name
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary disabled:opacity-50"
          >
            {isSubmitting ? 'Saving…' : 'Send Message'}
          </button>
        </div>
      </form>
    </div>
  );
};

const EventChoice = ({ id, label, value, onChange, yesLabel = 'Will attend' }) => (
  <fieldset className="rsvp-event-choice">
    <legend>{label}</legend>
    <RadioGroup
      value={value || ''}
      onValueChange={onChange}
      className="rsvp-choice-grid"
    >
      <div>
        <RadioGroupItem value="yes" id={`${id}-yes`} />
        <Label htmlFor={`${id}-yes`} className="rsvp-choice">
          {yesLabel}
        </Label>
      </div>
      <div>
        <RadioGroupItem value="no" id={`${id}-no`} />
        <Label htmlFor={`${id}-no`} className="rsvp-choice">
          Unable to attend
        </Label>
      </div>
    </RadioGroup>
  </fieldset>
);

const AttendanceCard = ({ response, onChange, isPlusOne = false }) => (
  <article className={`rsvp-guest-card ${isPlusOne ? 'rsvp-guest-card--plus-one' : ''}`}>
    {isPlusOne ? (
      <div className="mb-7">
        <Label htmlFor="plus-one-name">Guest’s full name</Label>
        <Input
          id="plus-one-name"
          type="text"
          value={response.name || ''}
          onChange={(event) => onChange('name', event.target.value)}
          placeholder="Guest’s name"
          autoComplete="name"
        />
      </div>
    ) : (
      <h3 className="font-giaza text-2xl md:text-3xl text-forest mb-7">
        {fullName(response)}
      </h3>
    )}

    <div className="space-y-7">
      <EventChoice
        id={`${isPlusOne ? 'plus-one' : response.member_id}-friday`}
        label="Friday · Welcome Reception"
        value={response.welcome_party}
        onChange={(value) => onChange('welcome_party', value)}
      />
      <EventChoice
        id={`${isPlusOne ? 'plus-one' : response.member_id}-saturday`}
        label="Saturday · Ceremony & Reception"
        value={
          response.attendance === 'accept'
            ? 'yes'
            : response.attendance === 'decline'
              ? 'no'
              : ''
        }
        onChange={(value) =>
          onChange('attendance', value === 'yes' ? 'accept' : 'decline')
        }
      />
    </div>
  </article>
);

const MealAndDietaryCard = ({ response, onChange, isPlusOne = false }) => (
  <article className={`rsvp-guest-card ${isPlusOne ? 'rsvp-guest-card--plus-one' : ''}`}>
    <h3 className="font-giaza text-2xl md:text-3xl text-forest mb-7">
      {isPlusOne ? response.name : fullName(response)}
    </h3>

    {response.attendance === 'accept' && (
      <fieldset className="rsvp-event-choice mb-7">
        <legend>Saturday meal</legend>
        <RadioGroup
          value={response.meal_choice || ''}
          onValueChange={(value) => onChange('meal_choice', value)}
          className="rsvp-meal-grid"
        >
          {MEALS.map((meal) => {
            const id = `${isPlusOne ? 'plus-one' : response.member_id}-${meal.value}`;
            return (
              <div key={meal.value}>
                <RadioGroupItem value={meal.value} id={id} />
                <Label htmlFor={id} className="rsvp-choice">
                  {meal.label}
                </Label>
              </div>
            );
          })}
        </RadioGroup>
      </fieldset>
    )}

    <div>
      <Label htmlFor={`${isPlusOne ? 'plus-one' : response.member_id}-dietary`}>
        Allergies or dietary restrictions
      </Label>
      <Textarea
        id={`${isPlusOne ? 'plus-one' : response.member_id}-dietary`}
        value={response.dietary_restrictions || ''}
        onChange={(event) => onChange('dietary_restrictions', event.target.value)}
        placeholder="Leave blank if none"
        rows={3}
        maxLength={500}
      />
    </div>
  </article>
);

const PersonSummary = ({ response, isPlusOne = false }) => {
  const friday = response.welcome_party === 'yes' ? 'Attending' : 'Not attending';
  const saturday = response.attendance === 'accept' ? 'Attending' : 'Not attending';

  return (
    <article className="rsvp-review-person">
      <h4>{isPlusOne ? `${response.name} (Guest)` : fullName(response)}</h4>
      <dl>
        <div>
          <dt>Friday</dt>
          <dd>{friday}</dd>
        </div>
        <div>
          <dt>Saturday</dt>
          <dd>{saturday}</dd>
        </div>
        {response.attendance === 'accept' && (
          <div>
            <dt>Meal</dt>
            <dd>
              {MEALS.find((meal) => meal.value === response.meal_choice)?.label ||
                'Not selected'}
            </dd>
          </div>
        )}
        {response.dietary_restrictions && (
          <div>
            <dt>Dietary notes</dt>
            <dd>{response.dietary_restrictions}</dd>
          </div>
        )}
      </dl>
    </article>
  );
};

const HouseholdRSVP = ({
  household,
  accessToken,
  submittedBy,
  onComplete,
  onBackToLookup,
  onSessionExpired,
}) => {
  const existing = household.rsvp_data || {};
  const members = household.guests || household.members || [];
  const [stage, setStage] = useState('attendance');
  const [responses, setResponses] = useState(() =>
    members.map((member) =>
      createResponse(
        member,
        existing.responses?.find((response) => response.member_id === member.id)
      )
    )
  );
  const [showPlusOne, setShowPlusOne] = useState(Boolean(existing.plus_one?.name));
  const [plusOne, setPlusOne] = useState(() => ({
    name: existing.plus_one?.name || '',
    attendance: existing.plus_one?.attendance || '',
    welcome_party: existing.plus_one?.welcome_party || '',
    meal_choice: existing.plus_one?.meal_choice || '',
    dietary_restrictions: existing.plus_one?.dietary_restrictions || '',
  }));
  const [contactEmail, setContactEmail] = useState(existing.contact_email || '');
  const [contactPhone, setContactPhone] = useState(existing.contact_phone || '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const attendees = useMemo(
    () => [
      ...responses.filter(
        (response) =>
          response.attendance === 'accept' || response.welcome_party === 'yes'
      ),
      ...(showPlusOne &&
      (plusOne.attendance === 'accept' || plusOne.welcome_party === 'yes')
        ? [{ ...plusOne, isPlusOne: true }]
        : []),
    ],
    [responses, showPlusOne, plusOne]
  );

  const updateResponse = (index, field, value) => {
    setResponses((current) =>
      current.map((response, responseIndex) => {
        if (responseIndex !== index) return response;
        const updated = { ...response, [field]: value };
        if (field === 'attendance' && value === 'decline') {
          updated.meal_choice = '';
        }
        return updated;
      })
    );
  };

  const updatePlusOne = (field, value) => {
    setPlusOne((current) => {
      const updated = { ...current, [field]: value };
      if (field === 'attendance' && value === 'decline') {
        updated.meal_choice = '';
      }
      return updated;
    });
  };

  const validateAttendance = () => {
    for (const response of responses) {
      if (!response.welcome_party || !response.attendance) {
        toast.error(`Please answer both event questions for ${fullName(response)}.`);
        return false;
      }
    }
    if (showPlusOne) {
      if (!plusOne.name.trim()) {
        toast.error('Please enter your guest’s full name.');
        return false;
      }
      if (!plusOne.welcome_party || !plusOne.attendance) {
        toast.error('Please answer both event questions for your guest.');
        return false;
      }
    }
    return true;
  };

  const validateDetails = () => {
    for (const response of responses) {
      if (response.attendance === 'accept' && !response.meal_choice) {
        toast.error(`Please select a meal for ${fullName(response)}.`);
        return false;
      }
    }
    if (
      showPlusOne &&
      plusOne.attendance === 'accept' &&
      !plusOne.meal_choice
    ) {
      toast.error('Please select a meal for your guest.');
      return false;
    }
    return true;
  };

  const continueFromAttendance = () => {
    if (!validateAttendance()) return;
    setStage(attendees.length > 0 ? 'details' : 'review');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const continueFromDetails = () => {
    if (!validateDetails()) return;
    setStage('review');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!contactEmail.trim()) {
      toast.error('Enter an email address for your confirmation.');
      return;
    }
    if (!validateAttendance() || !validateDetails()) return;

    setIsSubmitting(true);
    const submission = {
      household_id: household.id,
      responses,
      plus_one: showPlusOne ? plusOne : null,
      contact_email: contactEmail.trim(),
      contact_phone: contactPhone.trim() || null,
      submitted_by: `${submittedBy.firstName} ${submittedBy.lastName}`.trim(),
    };

    try {
      const response = await fetch(`${API}/rsvp/household`, {
        method: 'POST',
        headers: tokenHeaders(accessToken),
        body: JSON.stringify(submission),
      });
      if (response.status === 401) {
        onSessionExpired();
        return;
      }
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.detail || 'Submission failed');
      }
      onComplete(submission, result);
    } catch (error) {
      toast.error(error.message || 'We could not save your RSVP. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const backFromReview = () => {
    setStage(attendees.length > 0 ? 'details' : 'attendance');
  };

  return (
    <div className="rsvp-step max-w-3xl mx-auto">
      <Progress current={stage} />
      <div className="text-center mb-9">
        <p className="eyebrow text-olive mb-4">RSVP For</p>
        <h2 className="font-giaza text-3xl md:text-4xl text-forest mb-3">
          {household.label}
        </h2>
        <p className="rsvp-step__copy">
          {household.has_rsvpd
            ? 'Your previous response is shown below. You can update it at any time.'
            : `Please reply by ${RSVP_DEADLINE}.`}
        </p>
      </div>

      {stage === 'attendance' && (
        <div>
          <div className="rsvp-section-intro">
            <h3>Who can join us?</h3>
            <p>Please respond to Friday and Saturday separately for each guest.</p>
          </div>
          <div className="space-y-4">
            {responses.map((response, index) => (
              <AttendanceCard
                key={response.member_id}
                response={response}
                onChange={(field, value) => updateResponse(index, field, value)}
              />
            ))}
          </div>

          {household.allows_plus_one && (
            <div className="mt-5 space-y-4">
              <div className="rsvp-plus-one-toggle">
                <div>
                  <p className="font-giaza text-2xl text-forest">Your Guest</p>
                  <p className="text-base text-forest/70">
                    Your invitation includes one additional guest.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPlusOne((current) => !current)}
                  className={showPlusOne ? 'btn-primary' : 'btn-outline'}
                >
                  {showPlusOne ? 'Remove Guest' : 'Add Guest'}
                </button>
              </div>
              {showPlusOne && (
                <AttendanceCard
                  response={plusOne}
                  onChange={updatePlusOne}
                  isPlusOne
                />
              )}
            </div>
          )}

          <div className="rsvp-actions mt-8">
            <button type="button" onClick={onBackToLookup} className="btn-outline">
              Back
            </button>
            <button type="button" onClick={continueFromAttendance} className="btn-primary">
              Continue
            </button>
          </div>
        </div>
      )}

      {stage === 'details' && (
        <div>
          <div className="rsvp-section-intro">
            <h3>Meals & dietary needs</h3>
            <p>
              Choose a Saturday meal where needed and add any allergies or
              dietary restrictions for each attending guest.
            </p>
          </div>
          <div className="space-y-4">
            {responses
              .map((response, index) => ({ response, index }))
              .filter(
                ({ response }) =>
                  response.attendance === 'accept' ||
                  response.welcome_party === 'yes'
              )
              .map(({ response, index }) => (
                <MealAndDietaryCard
                  key={response.member_id}
                  response={response}
                  onChange={(field, value) => updateResponse(index, field, value)}
                />
              ))}
            {showPlusOne &&
              (plusOne.attendance === 'accept' ||
                plusOne.welcome_party === 'yes') && (
                <MealAndDietaryCard
                  response={plusOne}
                  onChange={updatePlusOne}
                  isPlusOne
                />
              )}
          </div>
          <div className="rsvp-actions mt-8">
            <button
              type="button"
              onClick={() => setStage('attendance')}
              className="btn-outline"
            >
              Back
            </button>
            <button type="button" onClick={continueFromDetails} className="btn-primary">
              Continue
            </button>
          </div>
        </div>
      )}

      {stage === 'review' && (
        <form onSubmit={handleSubmit}>
          <div className="rsvp-section-intro">
            <h3>Review your response</h3>
            <p>Confirm the details below, then tell us where to send your receipt.</p>
          </div>

          <div className="rsvp-review-list">
            {responses.map((response) => (
              <PersonSummary key={response.member_id} response={response} />
            ))}
            {showPlusOne && <PersonSummary response={plusOne} isPlusOne />}
          </div>

          <div className="rsvp-contact-card rsvp-form">
            <div>
              <Label htmlFor="rsvp-contact-email">Confirmation email</Label>
              <Input
                id="rsvp-contact-email"
                type="email"
                value={contactEmail}
                onChange={(event) => setContactEmail(event.target.value)}
                autoComplete="email"
                placeholder="you@example.com"
                required
              />
            </div>
            <div>
              <Label htmlFor="rsvp-contact-phone">Mobile number (optional)</Label>
              <Input
                id="rsvp-contact-phone"
                type="tel"
                value={contactPhone}
                onChange={(event) => setContactPhone(event.target.value)}
                autoComplete="tel"
                placeholder="For important weekend updates"
              />
            </div>
          </div>

          <div className="rsvp-actions mt-8">
            <button type="button" onClick={backFromReview} className="btn-outline">
              Back
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary disabled:opacity-50"
            >
              {isSubmitting
                ? 'Saving Your RSVP…'
                : household.has_rsvpd
                  ? 'Update RSVP'
                  : 'Submit RSVP'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

const Confirmation = ({ submission, result, onStartOver }) => (
  <div className="rsvp-step max-w-2xl mx-auto text-center">
    <div className="rsvp-success-mark" aria-hidden="true">
      ✓
    </div>
    <p className="eyebrow text-olive mb-4">
      {result.updated ? 'Response Updated' : 'Response Received'}
    </p>
    <h2 className="font-giaza text-3xl md:text-4xl text-forest mb-4">
      Thank You
    </h2>
    <p className="rsvp-step__copy mb-8">
      {result.confirmation_email_sent
        ? `A confirmation has been sent to ${submission.contact_email}.`
        : 'Your RSVP has been saved. You can return with the invitation access code and a guest name if you need to make a change.'}
    </p>

    <div className="rsvp-summary">
      <h3 className="eyebrow text-olive mb-5">Your Response</h3>
      <div className="rsvp-review-list">
        {submission.responses.map((response) => (
          <PersonSummary key={response.member_id} response={response} />
        ))}
        {submission.plus_one && (
          <PersonSummary response={submission.plus_one} isPlusOne />
        )}
      </div>
    </div>

    <button type="button" onClick={onStartOver} className="btn-outline mt-8">
      Find Another Invitation
    </button>
  </div>
);

export default function RSVPPage() {
  const [accessToken, setAccessToken] = useState(
    () => sessionStorage.getItem(RSVP_TOKEN_KEY) || ''
  );
  const [step, setStep] = useState('lookup');
  const [household, setHousehold] = useState(null);
  const [guestInfo, setGuestInfo] = useState(null);
  const [submittedBy, setSubmittedBy] = useState({ firstName: '', lastName: '' });
  const [submission, setSubmission] = useState(null);
  const [submissionResult, setSubmissionResult] = useState(null);

  const returnToLookup = () => {
    setStep('lookup');
    setHousehold(null);
    setGuestInfo(null);
  };

  const expireSession = () => {
    sessionStorage.removeItem(RSVP_TOKEN_KEY);
    setAccessToken('');
    returnToLookup();
    toast.error('Your RSVP session expired. Please enter the access code again.');
  };

  return (
    <div className="rsvp-page min-h-screen" data-testid="rsvp-page">
      <header className="page-hero rsvp-page-hero">
        <div className="page-hero__inner">
          <div>
            <p className="eyebrow text-olive mb-4">April 24, 2027</p>
            <h1 className="page-title">RSVP</h1>
          </div>
          <div className="rsvp-hero__copy">
            <p>We hope you can celebrate with us in Baltimore.</p>
            <p className="rsvp-deadline">Please reply by {RSVP_DEADLINE}.</p>
          </div>
        </div>
      </header>

      <section className="rsvp-content">
        <div className="rsvp-panel">
          {step === 'lookup' && (
            <InvitationLookup
              accessToken={accessToken}
              onAccessToken={setAccessToken}
              onHouseholdFound={(foundHousehold, name) => {
                setHousehold(foundHousehold);
                setSubmittedBy(name);
                setStep('household');
              }}
              onNotFound={(info) => {
                setGuestInfo(info);
                setStep('contact');
              }}
            />
          )}

          {step === 'contact' && guestInfo && (
            <ContactCoupleStep guestInfo={guestInfo} onBack={returnToLookup} />
          )}

          {step === 'household' && household && (
            <HouseholdRSVP
              household={household}
              accessToken={accessToken}
              submittedBy={submittedBy}
              onBackToLookup={returnToLookup}
              onSessionExpired={expireSession}
              onComplete={(savedSubmission, result) => {
                setSubmission(savedSubmission);
                setSubmissionResult(result);
                setStep('confirmation');
              }}
            />
          )}

          {step === 'confirmation' && submission && submissionResult && (
            <Confirmation
              submission={submission}
              result={submissionResult}
              onStartOver={returnToLookup}
            />
          )}
        </div>
      </section>
    </div>
  );
}
